import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getCurrentUser, hasOAuthHashSession } from '@/lib/apiService';
import { getDefaultAuthenticatedRoute } from '@/lib/accessControl';
import { CONSENT_REQUIRED_ROUTE, readConsentGateState } from '@/lib/consentGate';
import { isDemoModeEnabled } from '@/demo/config';
import { hasExpiredStoredAuthSession } from '@/lib/authSession';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const location = useLocation();
  const isDemoMode = isDemoModeEnabled();

  useEffect(() => {
    const checkAuth = () => {
      if (isDemoMode) {
        setRedirectPath(null);
        setIsLoading(false);
        return;
      }

      if (hasOAuthHashSession()) {
        setRedirectPath(null);
        setIsLoading(false);
        return;
      }

      // An expired JWT must not bounce the user back into the app.
      const authenticated = isAuthenticated() && !hasExpiredStoredAuthSession();
      const user = getCurrentUser();
      const isOnboardingRoute = location.pathname.startsWith('/onboarding');
      const isSignupCompletionRoute = location.pathname === '/signup/step-1';
      
      if (authenticated && user) {
        const consentState = readConsentGateState();
        if (consentState?.accessBlocked) {
          setRedirectPath(CONSENT_REQUIRED_ROUTE);
          setIsLoading(false);
          return;
        }

        if (!isOnboardingRoute && !isSignupCompletionRoute) {
          setRedirectPath(getDefaultAuthenticatedRoute(user));
          setIsLoading(false);
          return;
        }

        setRedirectPath(null);
      } else {
        setRedirectPath(isOnboardingRoute ? '/login' : null);
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, [isDemoMode, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (hasOAuthHashSession()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Signing you in...</p>
        </div>
      </div>
    );
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};
